import React from "react"
import { useDispatch } from "react-redux"
import Head from "next/head"
import { VerticalTimeline } from "react-vertical-timeline-component"

import { Button, Container, Card, Col, Row } from "react-bootstrap"

import {
    toggleMakeEnquiryModal,
    setMakeEnquiryReqType,
} from "@/slices/makeEnquiryModalSlice"
import { toggleValuationModal } from "@/slices/valuationModalSlice"

import MiscHeroImage from "@/main/miscHeroImage"
import CharacteristicsCarousel from "@/homeOwners/characteristicsCarousel"
import CharacteristicsStrip from "@/homeOwners/characteristicsStrip"
import TimelineElement from "@/homeOwners/timelineElement"
import InterestedCard from "@/homeOwners/interestedCard"
import ReviewCard from "@/homeOwners/reviewCard"

function SellersScreen() {
    const dispatch = useDispatch()

    const url = "https://webbjenkins.co.uk/sellers/"
    const title = "Selling Your Property on the Isle of Wight - Webb Jenkins"
    const description =
        "Get that property sold. We guide you through the entire property sales process, making the experience as easy and comfortable as possible for our clients."
    const imgUrl =
        "https://webb-jenkins-static-164679926.imgix.net/socials/webbjenkins-selling-social.jpg"
    const imgWidth = 1200
    const imgHeight = 630
    const imgAlt = "Webb Jenkins logo with selling your property caption"

    const description1 =
        "A local independent estate agency since 1998, our knowledge of the local area is unrivalled"
    const description2 =
        "Guiding you through the entire sales process, offering expert advice along the way"
    const description3 =
        "Active members of relevant bodies such as The Property Ombudsman Scheme, giving you peace of mind"
    const description4 =
        "Updating you at every step of the process so that you feel completely included and informed"

    return (
        <>
            <Head>
                <link rel="canonical" href={url} />
                <title>{title}</title>
                <meta name="description" content={description} />

                <meta property="fb:app_id" content="362437448786240" />
                <meta property="og:title" content={title} />
                <meta property="og:image:url" content={imgUrl} />
                <meta property="og:image:width" content={imgWidth} />
                <meta property="og:image:height" content={imgHeight} />
                <meta property="og:image:alt" content={imgAlt} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image:src" content={imgUrl} />
                <meta name="twitter:site" content="@WebbJenkinsIOW" />
            </Head>

            <MiscHeroImage
                title="Selling your property"
                subtitle="We make the process easy. Our team handle your sale from start to finish, taking the stress off your shoulders"
                image="property-seller-relaxing.jpg"
                alt="Property seller relaxing on the sofa smiling and checking their phone"
                position="center"
                buttonText="Get a free valuation"
                buttonOnClick={() => dispatch(toggleValuationModal(true))}
            />
            <Container fluid="md" as={Card} className="card-overlap-2 p-0">
                <Card.Body className="px-0">
                    <Card.Title>
                        <h2 className="text-center">
                            What you can expect from us
                        </h2>
                    </Card.Title>
                </Card.Body>
            </Container>

            <Container fluid="md" as={Card} className="d-sm-none mt-4 p-0">
                <Card.Body className="px-0">
                    <CharacteristicsCarousel
                        description1={description1}
                        description2={description2}
                        description3={description3}
                        description4={description4}
                    />
                </Card.Body>
            </Container>

            <CharacteristicsStrip
                description1={description1}
                description2={description2}
                description3={description3}
                description4={description4}
            />

            <Container fluid="md" className=" mt-4">
                <Row>
                    <Col className="text-center">
                        <Button
                            size="lg"
                            variant="warning"
                            onClick={() => {
                                dispatch(toggleMakeEnquiryModal(true))
                                dispatch(setMakeEnquiryReqType("general"))
                            }}
                            className="px-4 rounded-0 "
                        >
                            <span className="text-bold text-white">
                                Make enquiry
                            </span>
                        </Button>
                    </Col>
                </Row>
            </Container>

            <Container fluid="md" as={Card} className="p-0 mt-5">
                <Card.Body>
                    <Card.Title>
                        <h2 className="text-center">The sales process</h2>
                    </Card.Title>
                </Card.Body>
            </Container>
            <Container fluid="md" className="mt-4 ">
                <VerticalTimeline
                    layout={"1-column-left"}
                    animate={false}
                    className="m-0 w-100 mw-100"
                >
                    <TimelineElement
                        position="left"
                        sequence={1}
                        image="sellers/having-property-valued.jpg"
                        alt="Prospective seller having their property valued"
                        title="Have your property valued"
                        description="We offer a free, no obligation valuation of your property. Simply call or message us and we will set a convenient time for us to visit and appraise your property."
                    />

                    <TimelineElement
                        position="right"
                        sequence={2}
                        image="sellers/listing-a-property.jpg"
                        alt="Sellers looking at their high spec property listing"
                        title="List your property"
                        description="Once instructed by you, we will list your property not only on our own state of the art website, but all the major property websites, giving you maximum exposure locally and across the UK. We will prepare at no additional costs the property particulars, photography, floor plans and website listings."
                    />

                    <TimelineElement
                        position="left"
                        sequence={3}
                        image="sellers/viewing-a-property.jpg"
                        alt="Couple being shown around the property"
                        title="Viewings"
                        description="A member of our expert team will attend all viewings with potential buyers. Honest and accurate feedback will be given after each viewing."
                    />

                    <TimelineElement
                        position="right"
                        sequence={4}
                        image="sellers/recieving-offers-on-property.jpg"
                        alt="Seller considering offers and agreeing to a price"
                        title="Offers"
                        description="When presenting you with offers, you can be assured that the client will be fully vetted in relation to their ability to purchase, current situation and anticipated timescales."
                    />

                    <TimelineElement
                        position="left"
                        sequence={5}
                        image="sellers/property-sale.jpg"
                        alt="Exchanging house keys to the new buyer"
                        title="The sale, exchange & completion"
                        description="We will handle on your behalf all aspects of the sales process by liaising with solicitors, mortgage lenders, surveyors and other parties that may be involved in the chain. You can sit back and relax in the knowledge that our team are on top of things at all times. Once we reach the point of exchanging contracts, we will help to negotiate a mutually agreed date for completion."
                    />
                </VerticalTimeline>
            </Container>
            <Container fluid="md">
                <Row className="justify-content-between d-flex">
                    <InterestedCard
                        title="Ready to sell your property?"
                        buttonTitle="Get a free valuation"
                    />
                    <ReviewCard
                        review="We have used Webb Jenkins a number of times over the last 20 years, they have always gone over and above our expectations and nothing is ever too much trouble for them. We have confidently recommended them to friends and family who have been equally impressed by their efforts to put together a successful sale with regular updates and advice."
                        reviewer="Tyler & Nikki"
                    />
                </Row>
            </Container>
        </>
    )
}

export default SellersScreen
